/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { PlatformIccidPoolNumber } from '../../../interfaces/platform/PlatformIccidPoolNumber';
import { TableBody, TableHead } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import I18n from '../../../components/Translate/I18n';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import ViewButton from '../../../components/Buttons/ViewButton';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { IccidMvnoStatusHelper } from '../../lib/search/IccidMvnoStatusHelper';
import { SimCardTypeHelper } from '../../../modules/storage/SimCardTypeHelper';
import i18next from 'i18next';
import { SimCardMnoTariffLoader } from '../../../modules/tariffs/SimCardMnoTariffLoader';
import { RmoListHelper } from '../../../modules/rmo/RmoListHelper';
import { iccidStatus } from '../../../helpers/iccidStatus';
import { IccidState } from '../imsi/IccidState';
import { PlatformRmoCollectionInterface, PlatformRmoInterface } from '../../../interfaces/collections/PlatformRmoInterface';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { PlatformRmoType } from '../../../interfaces/collections/PliCbd2CollectionInterface';
import { StoreStackReducerAction, StoreStackReducerType } from '../../../modules/lib/StoreStackReducer';
import OPL_STYLE from '../../../assets/themes/OPL/OplStyle';

const _ = require('underscore');

export type StorageIccidSuperUserItemType = {
    classes: any;
    poolItems: Array<PlatformIccidPoolNumber> | null;
    viewCallback: (iri: { uri: string }, event: any) => void;
    firstLoad: () => void;
    setActiveRow: (id: number) => void;
    activeRow: number;
    updateOrderList: (orders: Array<Order>) => void;
    updateFilterList: (filters: Array<Filter>) => void;
    filters: Array<Filter>;
    orders: Array<Order>;
    iccidStatusHelper: IccidMvnoStatusHelper;
    simCardMnoTariffLoader: SimCardMnoTariffLoader;
};

export const StorageIccidSuperUserItem: FunctionComponent<StorageIccidSuperUserItemType> = ({
    poolItems,
    classes,
    viewCallback,
    updateOrderList,
    updateFilterList,
    setActiveRow,
    activeRow,
    firstLoad,
    filters,
    orders,
    iccidStatusHelper,
    simCardMnoTariffLoader,
}): React.ReactElement | null => {
    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableRowRoot = { root: classes.tableRowRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const dispatch = useDispatch();
    const [rmoModels, setRmoModels] = useState<PlatformRmoCollectionInterface | null>(null);
    const [selectRmoPartner, setSelectRmoPartner] = useState<Array<HeaderCheckBoxSelectListElement>>([]);
    const [selectRmoMvne, setSelectRmoMvne] = useState<Array<HeaderCheckBoxSelectListElement>>([]);
    const [statusList, setStatusList] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);

    useEffect(() => {
        if (!rmoModels) {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_rmos')),
                'get_platform_rmo_models_partner_mvne_list',
                [
                    { value: false, name: 'deleted' },
                    { name: 'active', value: 'true' },
                ] as Array<Filter>,
                [{ name: 'description', value: 'asc' }] as Array<Order>,
                (data: PlatformRmoCollectionInterface): void => {
                    setRmoModels(data);
                    dispatch({
                        type: StoreStackReducerAction.LOAD_PLATFORM_RMO_MODEL_PARTNER_MVNE_LIST,
                        platformRmoCollection: data,
                    } as StoreStackReducerType);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, rmoModels]);

    useEffect(() => {
        if (!rmoModels) {
            return;
        }
        const selectMvne: Array<HeaderCheckBoxSelectListElement> = [];
        const selectPartner: Array<HeaderCheckBoxSelectListElement> = [];
        for (let i = 0; i < rmoModels['hydra:member'].length; i++) {
            const model = rmoModels['hydra:member'][i];
            if (model.rmoType === PlatformRmoType.MVNE) {
                selectMvne.push({ name: model.description, value: model.id });
            }
            if (model.rmoType === PlatformRmoType.PARTNER) {
                selectPartner.push({ name: model.description, value: model.id });
            }
        }
        setSelectRmoMvne(selectMvne);
        setSelectRmoPartner(selectPartner);
    }, [rmoModels]);

    useEffect(() => {
        if (!statusList) {
            const status = new IccidState();
            setStatusList(status.getStatusList());
        }
    }, [statusList]);

    const getRmoDescription = (rmoId: number): string => {
        if (rmoModels) {
            const element = _.find(rmoModels['hydra:member'], (item: PlatformRmoInterface) => {
                return item.id === rmoId;
            });
            return element ? element.description : '';
        }
        return '';
    };

    return (
        <React.Fragment>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableStickyCommand} padding="none" align="left">
                        <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.iccid"
                            fieldName="iccid"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.imsi"
                            fieldName="imsi"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.iccid_type"
                            fieldName="iccidType"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerCheckBoxSimpleList"
                            elementList={SimCardTypeHelper.getList()}
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.msisdn"
                            fieldName="msisdnAll"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:sim.iccid_state"
                            fieldName="iccidState"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerCheckBoxSimpleList"
                            elementList={statusList}
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            title="prov:list.partner"
                            textAlign="left"
                            fieldName="ownerPlatformResellerId"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerPlatformReseller"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            title="pools:sim.service_profile"
                            textAlign="left"
                            fieldName="defaultTariff"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="select"
                            selectComponent="headerCheckBoxSimpleList"
                            elementList={simCardMnoTariffLoader.getHeaderList()}
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.puk1"
                            fieldName="puk1"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.puk2"
                            fieldName="puk2"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.pin1"
                            fieldName="pin1"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.pin2"
                            fieldName="pin2"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="number"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.created_at"
                            fieldName="createdAt"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="asc"
                            searchFieldType="date"
                            dateFormat="RRRR-MM-DD"
                            dateFromTo={true}
                            filters={filters}
                            sort={orders}
                            breakLine={true}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.share_date"
                            fieldName="sharedDate"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="asc"
                            searchFieldType="date"
                            dateFormat="RRRR-MM-DD"
                            dateFromTo={true}
                            filters={filters}
                            sort={orders}
                            breakLine={true}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="left">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="pools:pool.esimUpdate"
                            fieldName="esimUpdate"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="asc"
                            searchFieldType="date"
                            dateFormat="RRRR-MM-DD"
                            dateFromTo={true}
                            filters={filters}
                            sort={orders}
                            breakLine={true}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                            searchActionCallback={firstLoad}
                            nullable={true}
                        />
                    </TableCell>
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="simcard:list.status_mvno"*/}
                    {/*        fieldName="mvnoStatus"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="desc"*/}
                    {/*        searchFieldType="select"*/}
                    {/*        selectComponent="headerCheckBoxSimpleList"*/}
                    {/*        elementList={iccidStatusHelper.getList()}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*        nullable={true}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="pools:pool.mvne_status_rmo"*/}
                    {/*        fieldName="rmoModelMvne"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="desc"*/}
                    {/*        searchFieldType="select"*/}
                    {/*        selectComponent="headerCheckBoxSimpleList"*/}
                    {/*        elementList={selectRmoMvne}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="pools:pool.mvne_status_date_rmo"*/}
                    {/*        fieldName="rmoModelMvneSetup"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="asc"*/}
                    {/*        searchFieldType="date"*/}
                    {/*        dateFormat="RRRR-MM-DD"*/}
                    {/*        dateFromTo={true}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        breakLine={true}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*        nullable={true}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="pools:pool.partner_status_rmo"*/}
                    {/*        fieldName="rmoModelPartner"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="desc"*/}
                    {/*        searchFieldType="select"*/}
                    {/*        selectComponent="headerCheckBoxSimpleList"*/}
                    {/*        elementList={selectRmoPartner}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="pools:pool.partner_status_date_rmo"*/}
                    {/*        fieldName="rmoModelPartnerSetup"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="asc"*/}
                    {/*        searchFieldType="date"*/}
                    {/*        dateFormat="RRRR-MM-DD"*/}
                    {/*        dateFromTo={true}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        breakLine={true}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*        nullable={true}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="simcard:list.status_rmo"*/}
                    {/*        fieldName="rmoModel"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="desc"*/}
                    {/*        searchFieldType="select"*/}
                    {/*        selectComponent="headerCheckBoxSimpleList"*/}
                    {/*        elementList={RmoListHelper.getList()}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                    {/*<TableCell classes={tableStickyHeader} padding="none" align="left">*/}
                    {/*    <TableHeaderContentCell*/}
                    {/*        textAlign="left"*/}
                    {/*        title="simcard:list.status_date_rmo"*/}
                    {/*        fieldName="rmoModelSetup"*/}
                    {/*        showSort={true}*/}
                    {/*        onlyFilter={false}*/}
                    {/*        defaultDirection="asc"*/}
                    {/*        searchFieldType="date"*/}
                    {/*        dateFormat="RRRR-MM-DD"*/}
                    {/*        dateFromTo={true}*/}
                    {/*        filters={filters}*/}
                    {/*        sort={orders}*/}
                    {/*        breakLine={true}*/}
                    {/*        updateOrderList={updateOrderList}*/}
                    {/*        updateFilterList={updateFilterList}*/}
                    {/*        searchActionCallback={firstLoad}*/}
                    {/*        nullable={true}*/}
                    {/*    />*/}
                    {/*</TableCell>*/}
                </TableRow>
            </TableHead>
            <TableBody>
                {poolItems?.map((item) => {
                    let rowActiveColor = 'white';
                    if (activeRow === item.id) {
                        rowActiveColor = OPL_STYLE.TABLE.ACTIVE_ROW;
                    }
                    let statusString: string | null = item.mvnoStatus ? iccidStatusHelper.getName(item.mvnoStatus) : null;
                    const rmoModelName = item.rmoModel ? RmoListHelper.getName(item.rmoModel) : null;
                    if (statusString) {
                        statusString = i18next.t(statusString).toString();
                    }
                    return (
                        <TableRow
                            classes={tableRowRoot}
                            key={'key' + item.id}
                            hover
                            onClick={(): void => {
                                setActiveRow(item.id);
                            }}
                            style={{ backgroundColor: rowActiveColor }}
                        >
                            <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                                <ViewButton
                                    callback={viewCallback}
                                    callbackParam={{ uri: item['@id'] }}
                                    tooltipTitle="pools:add.iccid_view"
                                    tooltipPlacement="bottom-start"
                                />
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.iccid}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.imsi}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.iccidType}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.lastmsisdn === null && item.msisdn}
                                {item.lastmsisdn}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {<I18n trans={iccidStatus(item.iccidState)} />}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.ownerPlatformResellerName}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.defaultTariff && simCardMnoTariffLoader.getName(parseInt(item.defaultTariff))}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.puk1}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.puk2}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.pin1}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.pin2}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.createdAt && DateHelperTs.format(item.createdAt, 'YYYY-MM-DD')}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.sharedDate && DateHelperTs.format(item.sharedDate, 'YYYY-MM-DD')}
                            </TableCell>
                            <TableCell classes={tableCellRoot} padding="none" align="left">
                                {item.esimUpdate && DateHelperTs.format(item.esimUpdate, 'YYYY-MM-DD')}
                            </TableCell>
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {statusString}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {item.rmoModelMvne && getRmoDescription(item.rmoModelMvne)}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {item.rmoModelMvneSetup && DateHelperTs.format(item.rmoModelMvneSetup, 'YYYY-MM-DD')}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {item.rmoModelPartner && getRmoDescription(item.rmoModelPartner)}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {item.rmoModelPartnerSetup && DateHelperTs.format(item.rmoModelPartnerSetup, 'YYYY-MM-DD')}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {rmoModelName && <I18n trans={rmoModelName} />}*/}
                            {/*</TableCell>*/}
                            {/*<TableCell classes={tableCellRoot} padding="none" align="left">*/}
                            {/*    {item.rmoModelSetup && DateHelperTs.format(item.rmoModelSetup, 'YYYY-MM-DD')}*/}
                            {/*</TableCell>*/}
                        </TableRow>
                    );
                })}
            </TableBody>
        </React.Fragment>
    );
};
